import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuthContext } from '../../../context/auth.context'
import { lmsAdmin } from '../api'
import { useRoleOrg } from '../../../role/hook/role-org'
import { ROLE_ORG } from '../../../role/role.context'
import { connectionStatus, domainOf, logoFor } from '../format'
import { AgoCell, FAILED_STATE, LmsGrid, NameCell, PhoneLine, RowActions, agoOf, byField, byText, exportTime, msOf, useGridRows } from '../lms-grid'
import { LmsIcon } from '../lms-icon'
import { Pill } from '../../../shared/sso-workspace/pill'
import { useGridList } from '../../../shared/sso-workspace/fleet-grid'
import { useIsMobile } from '../../../shared/hooks/use-is-mobile'
import { TableAgClient } from '../../../shared/table-builder/table-ag-client'
import { ROW_HEIGHT_TWO_LINE } from '../../../shared/table-builder/table-ag'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../../shared/table-builder/table-columns'
import { IconLink } from '../../../shared/icon/icon'
import '../lms-admin.scss'
import { LmsTrail, lmsTrail } from '../lms-trail'

// Connections List (docs/08 §3, design edxr-connections-list-v2.0.html) —
// the entry point to every other LMS admin surface. States per the
// artifact's toolbar: populated, zero, at-scale (the grid's own pagination).

export const ViewConnectionsList = () => {
  const history = useHistory()
  const authContext = useAuthContext()
  // Pause and Resume need the LMS role's update access (the service answers 403
  // without it); a role that may only read is offered neither.
  const canChange = !!useRoleOrg(ROLE_ORG.LMS).accessUpdate

  // The grid's ready (and its footer's reload) is the loader (useGridRows).
  const { rows: connections, error, read: load, ready } = useGridRows(() => lmsAdmin.listConnections().then(result => result.connections || []))
  // a pause or resume that was refused
  const [actionError, updateActionError] = useState(null)
  const [statusFilter, updateStatusFilter] = useState('all')
  const tableRef = useRef()
  const mobile = useIsMobile()

  const counts = useMemo(() => {
    const all = connections || []
    return {
      all: all.length,
      active: all.filter(c => c.status === 'active').length,
      paused: all.filter(c => c.status === 'paused').length,
      archived: all.filter(c => c.status === 'archived').length
    }
  }, [connections])

  // The cells' words, flattened onto the row: the grid sorts, quick-filters
  // and exports the cell VALUE, so each column's field holds what it shows.
  const gridRows = useMemo(() => {
    if (!Array.isArray(connections)) return connections
    const shown = statusFilter === 'all' ? connections : connections.filter(c => c.status === statusFilter)
    return shown.map(connection => {
      const status = connectionStatus(connection.status)
      return {
        ...connection,
        connectionName: connection.name,
        domain: domainOf(connection.lms_base_url),
        // the name and the address in one value, so the grid's filter finds either
        connectionText: `${connection.name} ${domainOf(connection.lms_base_url)}`,
        platform: connection.preset ? connection.preset.name : '—',
        activityText: agoOf(connection.last_activity_at),
        activityMs: msOf(connection.last_activity_at),
        statusText: status.label,
        statusTone: status.tone,
        statusRank: status.rank,
        phone: mobile
      }
    })
  }, [connections, statusFilter, mobile])
  const gridList = useGridList(gridRows)

  const handleTogglePause = async connection => {
    updateActionError(null)
    try {
      if (connection.status === 'active') await lmsAdmin.pauseConnection(connection.id)
      else if (connection.status === 'paused') await lmsAdmin.resumeConnection(connection.id)
      await load()
    } catch (e) {
      updateActionError({
        connection,
        verb: connection.status === 'active' ? 'pause' : 'resume',
        // the service's own sentence, else the page's (a proxy's bare 502 or a
        // network failure has none worth showing)
        reason: e && e.readable ? `: ${e.message}` : '. Try again in a moment.'
      })
    }
  }

  // The zero state replaces the grid when there is nothing to list; a feature
  // that is switched off has no list to show.
  const featureOff = Boolean(error && error.code === 'FEATURE_DISABLED')
  const showGrid = !featureOff && !(Array.isArray(connections) && connections.length === 0)

  const customer = authContext.state.organizationName

  return (
    <div className="lms-admin" data-testid="lms-connections-list">
      <LmsTrail trail={lmsTrail({ url: '/org/lms/connections', text: 'Connections' })} />
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="page-header-title">
            LMS Connections
            <span className="page-header-customer">
              <LmsIcon name="building" />
              {customer}
            </span>
          </h1>
          <p className="page-header-sub">
            One connection for each of this organization's LMS environments. When a learner starts a course from
            the LMS, it opens in the EducationXR web viewer. A course with no web version opens in the app instead.
          </p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-outline-light btn-sm" type="button">
            <LmsIcon name="circle-info" className="me-1" />
            Setup guide
          </button>
          {connections && connections.length > 0 && (
            <button className="btn btn-light btn-sm" type="button" onClick={() => history.push('/org/lms/connections/new')}>
              <LmsIcon name="plus" className="me-1" />
              New connection
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error.code === 'FEATURE_DISABLED'
            ? "The LMS Connector isn't enabled for this organization yet — contact EducationXR to turn it on."
            : `Couldn't load connections: ${error.message}`}
        </div>
      )}

      {actionError && (
        <div className="alert alert-danger" role="alert">
          {`Couldn't ${actionError.verb} ${actionError.connection.name}${actionError.reason}`}
        </div>
      )}

      {connections && connections.length === 0 && !error && (
        <ZeroState
          onAdd={() => history.push('/org/lms/connections/new')}
          onPick={presetID => history.push(`/org/lms/connections/new?preset=${presetID}`)}
        />
      )}

      {showGrid && (
        <React.Fragment>
          {connections && connections.length > 0 && (
            <div className="filter-strip">
              <div className="filter-chips">
                {[
                  ['all', 'All', counts.all],
                  ['active', 'Active', counts.active],
                  ['paused', 'Paused', counts.paused],
                  ['archived', 'Archived', counts.archived]
                ].map(([key, label, count]) => (
                  <button
                    key={key}
                    type="button"
                    className={`filter-chip ${statusFilter === key ? 'active' : ''}`}
                    onClick={() => updateStatusFilter(key)}
                  >
                    {label} <span className="count">{count}</span>
                  </button>
                ))}
              </div>
              <div className="spacer" />
            </div>
          )}

          <LmsGrid title="connections" tableRef={tableRef} exportName="lms-connections">
            <TableAgClient
              ref={tableRef}
              apiList={gridList}
              onReady={ready}
              sortCol="connectionText"
              rowHeight={ROW_HEIGHT_TWO_LINE}
              onRowClicked={connection => history.push(`/org/lms/connections/${connection.id}`)}
              emptyState={connections === null ? FAILED_STATE : {
                Icon: IconLink,
                title: `No ${statusFilter === 'all' ? '' : `${statusFilter} `}connections`,
                message: statusFilter === 'all' ? 'Nothing to show yet.' : 'Choose "All" to see every connection.'
              }}
              noMatchState={{ message: 'No connections match. Clear the search or choose "All".' }}>
              <ColumnFormat
                field="connectionText"
                header="Connection"
                render={connectionRow}
                exportValue={exportConnection}
                comparator={byText('connectionName')}
                stacked
                minWidth={mobile ? 150 : 240}
                flex={2}
                sortable
              />
              <ColumnText field="platform" header="Platform" sortable hide={mobile} />
              <ColumnFormat
                field="activityText"
                header="Last activity"
                render={activityCell}
                exportValue={connection => exportTime(connection.last_activity_at)}
                comparator={byField('activityMs')}
                sortable
                hide={mobile}
              />
              <ColumnFormat field="statusText" header="Status" render={statusCell} comparator={byField('statusRank')} sortable hide={mobile} />
              <ColumnMenu
                menu={connection => (
                  <RowActions
                    rowId={connection.id}
                    rowName={connection.name}
                    actions={[{
                      label: connection.status === 'active' ? 'Pause' : 'Resume',
                      name: `${connection.status === 'active' ? 'Pause' : 'Resume'} ${connection.name}`,
                      show: canChange && connection.status !== 'archived',
                      onClick: () => handleTogglePause(connection)
                    }]}
                  />
                )}
              />
            </TableAgClient>
          </LmsGrid>
        </React.Fragment>
      )}
    </div>
  )
}

// The connection column's CSV value: its name and its LMS address, both parts
// of the cell.
export const exportConnection = connection => `${connection.name} (${connection.domain})`

// How long ago the connection was last active, with its freshness cue.
const activityCell = cell => (cell.data ? <AgoCell value={cell.data.last_activity_at} cue /> : '')

const statusCell = cell => (cell.data ? <Pill tone={cell.data.statusTone}>{cell.data.statusText}</Pill> : '')

// The connection's monogram, name (a link to its page) and address; on a phone
// the status pill and the platform instead, in place of the columns a phone has
// no room for.
const connectionRow = cell => {
  const connection = cell.data
  if (!connection) return ''
  const phone = connection.phone
    ? <PhoneLine pill={<Pill tone={connection.statusTone}>{connection.statusText}</Pill>}>{connection.platform}</PhoneLine>
    : null
  return (
    <NameCell
      logoName={connection.preset && connection.preset.name}
      name={connection.name}
      to={`/org/lms/connections/${connection.id}`}
      sub={connection.domain}
      phone={phone}
    />
  )
}

// ZeroState renders the first-run experience with the quick-start grid: a tile
// for each published preset (the platforms the LMS service offers an
// organization, the same list the connection form's picker reads), which opens
// the form with that platform chosen, and Other LMS, which opens it blank. No
// tile is marked Verified: nothing here says an LMS was tried until one is proven
// against a real sandbox of that LMS. A read that fails leaves Other LMS alone.
const ZeroState = ({ onAdd, onPick }) => {
  // null while the presets are being read
  const [presets, updatePresets] = useState(null)

  useEffect(() => {
    let current = true
    lmsAdmin
      .listPublishedPresets()
      .then(result => current && updatePresets(Array.isArray(result && result.presets) ? result.presets : []))
      .catch(() => current && updatePresets([]))
    return () => {
      current = false
    }
  }, [])

  return (
    <div className="zero-state" data-testid="lms-zero-state">
      <div className="zero-state-icon">
        <LmsIcon name="link" />
      </div>
      <h3>Connect your first LMS</h3>
      <p>
        An LMS connection tells EducationXR which platform to package your courses for. You only need to define
        each LMS once — every course you publish reuses the same connection settings.
      </p>
      <button className="btn btn-primary" type="button" onClick={onAdd}>
        <LmsIcon name="plus" className="me-1" />
        Add connection
      </button>

      <div className="quick-start" data-testid="lms-quick-start">
        <p className="quick-start-title">Quick start — pick your LMS</p>
        {presets === null ? (
          <p className="quick-start-note">Loading platforms…</p>
        ) : (
          <div className="platform-grid">
            {presets.map(preset => {
              const logo = logoFor(preset.name)
              return (
                <button key={preset.id} type="button" className="platform-tile" onClick={() => onPick(preset.id)}>
                  <span className={`conn-logo ${logo.className}`} aria-hidden="true">
                    {logo.text}
                  </span>
                  <span className="label">{preset.name}</span>
                </button>
              )
            })}
            <button type="button" className="platform-tile" onClick={onAdd}>
              <span className="conn-logo custom" aria-hidden="true">
                <LmsIcon name="code" />
              </span>
              <span className="label">Other LMS</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
